// ============================================================================
//  OBP — OFFRES QUANTITÉ — logique panier.
//  Binôme du bloc blocks/obp-offers.liquid. Même architecture que
//  assets/obp-addons.js et assets/gift-message.js (les références commentées).
// ============================================================================
//
//  PRINCIPE (v2 — robuste)
//  On ne manipule PAS le champ quantité du thème (son composant reprenait la
//  main et remettait 1 → les lots partaient à l'unité). À la place :
//  1. Le client choisit une offre (radio) et clique « Ajouter au panier ».
//  2. Le formulaire natif ajoute SA quantité (en général 1) et émet
//     `cart:update` avec { itemCount, source:'product-form-component' }.
//  3. On COMPLÈTE alors le panier : quantité manquante du produit principal
//     (offre 3 plateaux → +2) puis produit compagnon éventuel (offre mixte).
//  4. On émet un CartUpdateEvent sans `sections` : tiroir et pastille se
//     rafraîchissent d'eux-mêmes (y compris le tiroir EA Sticky Cart).
//
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

class OBPOffersComponent extends HTMLElement {
  /** @type {AbortController} recréé à chaque connexion (un signal aborté est définitif). */
  #abortController;
  #busy = false;

  /**
   * Radio à désélectionner si le clic en cours la visait déjà (façon Kaching).
   * ⚠ Un clic sur un <label> déclenche DEUX événements click (le label, puis le
   * clic transmis à l'input) : on mémorise l'état AVANT le clic (pointerdown)
   * et on n'agit que sur le clic transmis à l'input, sinon la sélection
   * s'annulait instantanément (bug « on ne peut plus cliquer »).
   */
  #pendingDeselect = null;

  /** Carte variante → {title, image} pour un produit multi-variantes (coffret Vol.1/Vol.2). */
  #variantInfo = null;

  /** Set d'IDs de variantes « livraison à domicile uniquement » (ex. grand plateau 42×31). */
  #homeOnly = new Set();

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    if (this.dataset.variantInfo) {
      try {
        this.#variantInfo = JSON.parse(this.dataset.variantInfo);
        document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
      } catch (error) {
        console.error('[OBP offres] data-variant-info illisible', error);
      }
    }
    // OBP : variantes « livraison à domicile uniquement » (ex. grand plateau 42×31).
    if (this.dataset.obpHomeOnly) {
      this.dataset.obpHomeOnly.split(',').forEach((id) => {
        const t = id.trim();
        if (t) this.#homeOnly.add(t);
      });
    }
    this.#preselectFromVariant();
    // Badge livraison : reflète l'état initial (défaut ou « domicile uniquement »).
    this.querySelectorAll('.obp-offers__offer').forEach((offer) => this.#updateShip(offer));
    // Cartes « ensemble » à double choix : calcule le prix total initial.
    this.querySelectorAll('input[data-total-mode]').forEach((input) => this.#recomputeTotal(input));
    // Cartes « N lots » : initialise l'état désactivé des compteurs.
    this.querySelectorAll('[data-obp-split]').forEach((c) => {
      const total = Number(c.dataset.splitTotal || 0);
      c.querySelectorAll('[data-obp-split-row]').forEach((r) => {
        const n = Number(r.querySelector('[data-obp-split-count]').textContent) || 0;
        r.querySelector('[data-obp-split-dec]').disabled = n <= 0;
        r.querySelector('[data-obp-split-inc]').disabled = n >= total;
      });
    });
    // Galerie complète : préfetch dès qu'un déclencheur existe (pill « both »
    // ou offre data-gallery-full) et que la galerie de la page est filtrée.
    if (
      this.querySelector('[data-obp-volume="both"], input[data-gallery-full]') &&
      this.#scope.querySelector('media-gallery[data-obp-variant-filter]')
    ) {
      this.#fetchAllGallery();
    }
    console.info('[OBP offres] prêt — produit', this.dataset.productId);
  }

  /**
   * Changement de variante sur la page (ex. coffret Vol.1 → Vol.2) : le déroulé
   * des offres suit — ligne principale = variante choisie, ligne compagnon des
   * offres « swap » = l'autre variante.
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!this.#variantInfo) return;
    if (detail?.data?.productId && String(detail.data.productId) !== String(this.dataset.productId)) return;
    const vid = String(detail?.resource?.id || this.#mainVariantId);
    const current = this.#variantInfo[vid];
    if (current) {
      this.querySelectorAll('[data-obp-main-title]').forEach((el) => { el.textContent = (current.title || '').replace(/ — /g, ' - '); });
      this.querySelectorAll('img[data-obp-main-img]').forEach((el) => { if (current.image) el.src = current.image; });
    }
    this.querySelectorAll('input[data-extra-swap]').forEach((input) => {
      try {
        const swapMap = JSON.parse(input.dataset.extraSwap || '{}');
        const other = this.#variantInfo?.[String(swapMap[vid])];
        if (!other) return;
        const offer = input.closest('.obp-offers__offer');
        offer?.querySelectorAll('[data-obp-swap-title]').forEach((el) => { el.textContent = (other.title || '').replace(/ — /g, ' - '); });
        offer?.querySelectorAll('img[data-obp-swap-img]').forEach((el) => { if (other.image) el.src = other.image; });
      } catch (error) {
        console.error('[OBP offres] mise à jour swap impossible', error);
      }
    });
    // Galerie : media-gallery.js (listener de la section, passé AVANT celui-ci)
    // vient de remettre la galerie filtrée de la variante — si la pill « Les 2
    // coffrets » est active, re-poser la galerie complète dans le même tick.
    this.#applyGalleryMode();
  };

  /**
   * Applique un préréglage de composition : coche la carte, marque le bouton
   * actif, et réécrit data-offer-qty / data-extra-qty sur la radio. Le moteur
   * d'ajout au panier lit ces attributs, il n'a pas besoin de connaître les
   * préréglages. Le prix affiché est identique d'un préréglage à l'autre :
   * on n'y touche pas.
   */
  #activatePreset(button) {
    const group = button.closest('[data-obp-presets]');
    const row = button.closest('.obp-offers__offer');
    if (!group || !row) return;
    const input = row.querySelector('input[type="radio"]');
    if (input && !input.disabled && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    group.querySelectorAll('[data-obp-preset]').forEach((b) => {
      const on = b === button;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (input) {
      input.dataset.offerQty = button.dataset.presetMain || '0';
      if (input.dataset.extraVariant) input.dataset.extraQty = button.dataset.presetExtra || '0';
      console.info('[OBP offres] préréglage :', input.dataset.offerQty, 'principal +', input.dataset.extraQty || 0, 'compagnon');
    }
  }

  #onPointerDown = (event) => {
    // Un clic sur une pill (volume OU choix d'ensemble) ne doit JAMAIS désélectionner la carte.
    if (event.target instanceof Element && event.target.closest('[data-obp-volume-group], [data-obp-pillgroup], [data-obp-split], [data-obp-presets]')) {
      this.#pendingDeselect = null;
      return;
    }
    const row = event.target.closest('.obp-offers__row');
    if (!row || !this.contains(row)) { this.#pendingDeselect = null; return; }
    const input = row.querySelector('input[type="radio"]');
    this.#pendingDeselect = input && input.checked ? input : null;
  };

  /** Re-cliquer l'offre sélectionnée la désélectionne (aucune offre = achat normal). */
  #onClick = (event) => {
    // Préréglage de composition (carte fusionnée) : ne change PAS le prix,
    // réécrit seulement la répartition portée par la radio de la carte.
    const preset = event.target instanceof Element && event.target.closest('[data-obp-preset]');
    if (preset && this.contains(preset)) {
      this.#activatePreset(preset);
      this.#pendingDeselect = null;
      return;
    }
    // Pill de volume (choix Vol.1/Vol.2 DANS la carte) : sélectionne la carte,
    // mémorise le volume, met à jour légende/vignettes/déroulé, proxe la variante.
    const pill = event.target instanceof Element && event.target.closest('[data-obp-volume]');
    if (pill && this.contains(pill)) {
      this.#activatePill(pill, true);
      this.#pendingDeselect = null;
      return;
    }
    // Carte « ensemble » : pastille de variante de la PAGE (proxy) ou du COMPAGNON.
    const mainPick = event.target instanceof Element && event.target.closest('[data-obp-main-pick]');
    if (mainPick && this.contains(mainPick)) {
      this.#activateMainPick(mainPick);
      this.#pendingDeselect = null;
      return;
    }
    const extraPick = event.target instanceof Element && event.target.closest('[data-obp-extra-pick]');
    if (extraPick && this.contains(extraPick)) {
      this.#activateExtraPick(extraPick);
      this.#pendingDeselect = null;
      return;
    }
    // Carte « N lots » à répartir : boutons +/- des compteurs.
    const incBtn = event.target instanceof Element && event.target.closest('[data-obp-split-inc]');
    const decBtn = event.target instanceof Element && event.target.closest('[data-obp-split-dec]');
    if ((incBtn || decBtn) && this.contains(incBtn || decBtn)) {
      this.#adjustSplit(incBtn || decBtn, incBtn ? 1 : -1);
      this.#pendingDeselect = null;
      return;
    }
    if (!(event.target instanceof HTMLInputElement)) return;
    if (this.#pendingDeselect === event.target) {
      event.target.checked = false;
    }
    this.#pendingDeselect = null;
    // Mode « constructeur » : une carte avec data-set-value pilote le sélecteur
    // de variantes natif (masqué ou non) — même mécanique qu'obp-format-picker.
    const input = event.target;
    if (input.checked && input.dataset.setValue) {
      this.#proxyVariant(input.dataset.setValue);
    }
    this.#syncGallery(input);
    this.#applyGalleryMode();
  };

  /** Clique le radio natif du sélecteur de variantes correspondant à la valeur d'option. */
  #proxyVariant(optionValue) {
    const native = Array.from(this.#scope.querySelectorAll('variant-picker input[type="radio"]'))
      .find((i) => i.value === optionValue);
    if (native && !native.checked) {
      native.click();
      console.info('[OBP offres] variante sélectionnée via carte :', optionValue);
    }
  }

  /** La galerie a-t-elle été déplacée sur une photo de formule ? (pour restaurer) */
  #galleryMoved = false;

  /**
   * Photo de galerie par formule : navigue la galerie média sur la photo dont
   * le nom de fichier commence par `match` (vignettes = boutons cliquables).
   */
  #showGalleryImage(match) {
    // ⚠ le thème rend DEUX galeries (mobile + desktop) : agir sur chacune,
    // via l'API select() du composant Slideshow d'Horizon (un scroll forcé
    // se fait « re-snapper » par l'état interne du composant).
    const contains = (img) => decodeURIComponent(img.currentSrc || img.src || '').includes(match);
    let moved = 0;
    this.#scope.querySelectorAll('media-gallery').forEach((gallery) => {
      const slideshow = gallery.querySelector('slideshow-component');
      const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
      const index = slides.findIndex((sl) => Array.from(sl.querySelectorAll('img')).some(contains));
      if (slideshow && typeof slideshow.select === 'function' && index >= 0) {
        slideshow.select(index, undefined, { animate: false });
        moved++;
      }
    });
    if (moved) {
      this.#galleryMoved = true;
      console.info('[OBP offres] galerie → photo de la formule :', match, `(${moved} galeries)`);
    }
  }

  /** Revient à la première photo si une photo de formule avait été affichée. */
  #restoreGallery() {
    if (!this.#galleryMoved) return;
    this.#scope.querySelectorAll('media-gallery slideshow-component').forEach((slideshow) => {
      if (typeof (/** @type {any} */ (slideshow)).select === 'function') {
        /** @type {any} */ (slideshow).select(0, undefined, { animate: false });
      }
    });
    this.#galleryMoved = false;
  }

  /** Applique la photo de galerie de la carte sélectionnée (ou restaure). */
  #syncGallery(input) {
    if (input.checked && input.dataset.galleryImage) {
      this.#showGalleryImage(input.dataset.galleryImage);
    } else {
      this.#restoreGallery();
    }
  }

  // --------------------------------------------------------------------------
  //  Galerie « Les 2 coffrets » : la galerie de la fiche est FILTRÉE côté
  //  serveur par variante (metafield custom.galerie_variante, cf. snippet
  //  product-media-gallery-content). La pill « both » n'ayant pas de variante
  //  propre, on remplace la galerie par sa version COMPLÈTE, rendue par le
  //  gabarit alternatif ?view=obp-galerie (galerie sans filtre) — même
  //  mécanique replaceWith que media-gallery.js sur variantUpdate. Le listener
  //  document passe après celui de la section : l'échange se fait dans le même
  //  tick que le re-render natif → aucun clignotement.
  // --------------------------------------------------------------------------

  /** @type {Promise<Document|null>|null} fetch (unique) du gabarit ?view=obp-galerie */
  #allGalleryPromise = null;

  /** Galeries filtrées remplacées en mode « both » : [{placed, saved}] pour restaurer sans re-render. */
  #savedGalleries = null;

  /**
   * La galerie doit-elle afficher la version COMPLÈTE ? (état lu dans le DOM)
   * Deux déclencheurs : la pill « Les 2 coffrets » (both) active sur la carte
   * cochée, OU une carte cochée marquée `data-gallery-full` (réglage
   * « Galerie complète quand la formule est cochée » — ex. plateau NDP + ses
   * 4 dessous de verre : la photo de l'ensemble passe en tête tant que
   * l'offre est cochée, et disparaît dès qu'on décoche).
   */
  get #bothMode() {
    const checked = this.querySelector('.obp-offers__offer input[type="radio"]:checked');
    if (!checked) return false;
    if ('galleryFull' in checked.dataset) return true;
    const offer = checked.closest('.obp-offers__offer');
    return !!offer?.querySelector('[data-obp-volume="both"].is-active');
  }

  /** Récupère (une seule fois) le document de la galerie complète. */
  #fetchAllGallery() {
    if (!this.#allGalleryPromise) {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('view', 'obp-galerie');
      this.#allGalleryPromise = fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((text) => new DOMParser().parseFromString(text, 'text/html'))
        .catch((error) => {
          console.error('[OBP offres] galerie complète indisponible', error);
          this.#allGalleryPromise = null;
          return null;
        });
    }
    return this.#allGalleryPromise;
  }

  /** Aligne la galerie sur le mode courant : « both » = complète, sinon filtrée. */
  async #applyGalleryMode() {
    if (!this.#scope.querySelector('media-gallery')) return;
    if (this.#bothMode) {
      const doc = await this.#fetchAllGallery();
      // l'état a pu changer pendant le fetch (clic sur une pill simple)
      if (!doc || !this.#bothMode) return;
      const source = doc.querySelector('media-gallery');
      if (!source) return;
      const galleries = Array.from(this.#scope.querySelectorAll('media-gallery')).filter(
        (g) => !g.hasAttribute('data-obp-all-media')
      );
      if (!galleries.length) return;
      this.#savedGalleries = this.#savedGalleries || [];
      galleries.forEach((g) => {
        const clone = /** @type {Element} */ (source.cloneNode(true));
        clone.setAttribute('data-obp-all-media', '');
        this.#savedGalleries.push({ placed: clone, saved: g });
        g.replaceWith(clone);
      });
      console.info('[OBP offres] galerie → toutes les photos (les 2 coffrets)');
    } else {
      // Retour à une pill simple : si un re-render natif (variantUpdate) a déjà
      // remis une galerie filtrée, il n'y a rien à faire ; sinon on restaure la
      // galerie filtrée sauvegardée (cas « même variante, pas de re-render »).
      let restored = 0;
      this.#scope.querySelectorAll('media-gallery[data-obp-all-media]').forEach((g) => {
        const saved = this.#savedGalleries?.find((s) => s.placed === g)?.saved;
        if (saved) {
          // ⚠ ré-insérer un CLONE : les composants Horizon créent leur
          // AbortController à la construction — le nœud d'origine, déjà
          // déconnecté une fois, aurait ses listeners définitivement morts.
          g.replaceWith(saved.cloneNode(true));
          restored++;
        }
      });
      this.#savedGalleries = null;
      if (restored) console.info('[OBP offres] galerie → photos de la variante');
    }
  }

  /** Rend la liste de châteaux : puces (UL grille) ou ligne compacte «  ·  » (autre élément). */
  #renderCastles(caption, names) {
    if (caption.tagName === 'UL') {
      caption.replaceChildren(
        ...names.map((name) => {
          const li = document.createElement('li');
          li.textContent = name;
          return li;
        })
      );
    } else {
      caption.textContent = names.join(' · ');
    }
  }

  /** Active une pill de volume : état visuel, carte cochée, textes/images/prix, proxy. */
  #activatePill(pill, proxy) {
    const offer = pill.closest('.obp-offers__offer');
    const input = offer?.querySelector('input[type="radio"]');
    if (!offer || !input) return;
    offer.querySelectorAll('[data-obp-volume]').forEach((p) => {
      p.classList.toggle('is-active', p === pill);
    });
    input.dataset.setValue = pill.dataset.setValue || '';
    if (!input.checked) input.checked = true;
    const caption = offer.querySelector('[data-obp-vol-caption]');
    const priceEl = offer.querySelector('.obp-offers__price');

    if (pill.dataset.obpVolume === 'both') {
      // « Les 2 » : 1re variante au formulaire + l'autre en compagnon,
      // prix affiché = both_price, puces = les châteaux des DEUX volumes.
      if (pill.dataset.bothExtra) {
        input.dataset.extraVariant = pill.dataset.bothExtra;
        input.dataset.extraQty = '1';
      }
      if (caption && this.#variantInfo) {
        const names = Object.values(this.#variantInfo).flatMap(
          (v) => (v.title.replace(/ — /g, ' - ').split(' - ')[1] || v.title).split(', ')
        );
        this.#renderCastles(caption, names);
      }
    } else {
      // pill de volume simple : si la carte a une pill « les 2 », retirer le compagnon
      // (le prix est repositionné plus bas depuis le prix de la variante).
      if (offer.querySelector('[data-obp-volume="both"]')) {
        delete input.dataset.extraVariant;
        delete input.dataset.extraQty;
      }
      const info = this.#variantInfo?.[String(pill.dataset.obpVolume)];
      if (info) {
        offer.querySelectorAll('[data-obp-row-title]').forEach((el) => { el.textContent = (info.title || '').replace(/ — /g, ' - '); });
        offer.querySelectorAll('img[data-obp-row-img], img.obp-offers__thumb').forEach((el) => {
          if (info.image) el.src = info.image;
        });
        if (caption) {
          const parts = info.title.replace(/ — /g, ' - ').split(' - ');
          this.#renderCastles(caption, (parts[1] || info.title).split(', '));
        }
      }
    }
    // Prix affiché de la carte (évolution B) : « les 2 » → both_price ; pill simple
    // → prix de la variante (permet 49 ↔ 65 ↔ 114 sur le plateau, variantes à prix ≠).
    // Sur le coffret (variantes à prix égal) le résultat est identique à avant.
    if (priceEl) {
      let displayPrice;
      if (pill.dataset.obpVolume === 'both') {
        // Prix « Les 2 » laissé vide dans les réglages = somme des prix des
        // variantes, calculée ici (reste juste si un prix change).
        displayPrice = pill.dataset.bothPrice;
        if (!displayPrice) {
          const sum = Object.values(this.#variantInfo || {}).reduce((acc, v) => acc + this.#parseMoney(v.price), 0);
          if (!isNaN(sum) && sum > 0) displayPrice = this.#formatMoney(sum);
        }
      } else {
        // Pill simple : si la carte a un PRIX FIXE (offre composée, ex. coffret + 4
        // grandes = 119 €), on le restaure ; sinon on suit le prix de la variante
        // (plateau 49/65/114). Corrige le bug « le prix tombe à 57 € au clic ».
        displayPrice =
          priceEl.dataset.obpFixedPrice ||
          this.#variantInfo?.[String(pill.dataset.obpVolume)]?.price;
      }
      // OBP 08/10/2026 : prix barré de la variante (compare_at_price), ex. plateau
      // Loire 49 € au lieu de 59 €. « Les 2 » : somme des barrés, seulement si au
      // moins une variante est barrée. Jamais sur une carte à prix fixe.
      let displayCompare = '';
      if (!priceEl.dataset.obpFixedPrice) {
        if (pill.dataset.obpVolume === 'both') {
          const vals = Object.values(this.#variantInfo || {});
          if (vals.some((v) => v.compare)) {
            const sum = vals.reduce((acc, v) => acc + this.#parseMoney(v.compare || v.price), 0);
            if (!isNaN(sum)) displayCompare = this.#formatMoney(sum);
          }
        } else {
          displayCompare = this.#variantInfo?.[String(pill.dataset.obpVolume)]?.compare || '';
        }
      }
      if (displayPrice) this.#setPrice(priceEl, displayPrice, displayCompare);
    }
    if (proxy && input.dataset.setValue) this.#proxyVariant(input.dataset.setValue);
    if (proxy) this.#syncGallery(input);
    this.#applyGalleryMode();
    this.#updateShip(offer);
  }

  /**
   * OBP : badge livraison de l'offre. Si une variante « domicile uniquement »
   * (ex. grand plateau 42×31, qui ne rentre pas en point relais) fait partie de
   * l'offre sélectionnée, le badge devient « Livraison à domicile offerte » ;
   * sinon il reprend son libellé par défaut (« ✓ Livraison offerte » ou rien).
   * On lit les IDs de variantes sur les pastilles ACTIVES de la carte (toutes
   * les pastilles portent leur id de variante).
   */
  #updateShip(offer) {
    const ship = offer?.querySelector('[data-obp-ship]');
    if (!ship) return;
    let isHome = false;
    if (this.#homeOnly.size) {
      const ids = new Set();
      offer
        .querySelectorAll('[data-obp-volume].is-active, [data-obp-main-pick].is-active, [data-obp-extra-pick].is-active')
        .forEach((p) => {
          const vid = p.dataset.obpVolume || p.dataset.obpMainPick || p.dataset.obpExtraPick;
          if (vid === 'both') {
            // « Les 2 » : les deux variantes du groupe sont au panier.
            [p.dataset.bothExtra, p.dataset.main2, p.dataset.extra, p.dataset.extra2].forEach(
              (x) => x && ids.add(String(x))
            );
          } else if (vid) {
            ids.add(String(vid));
          }
        });
      isHome = [...ids].some((id) => this.#homeOnly.has(id));
    }
    const def = ship.dataset.shipDefault || '';
    if (isHome) {
      ship.textContent = ship.dataset.shipHome || '✓ Livraison à domicile offerte';
      ship.hidden = false;
      ship.classList.add('obp-offers__ship--home');
    } else {
      ship.textContent = def;
      ship.hidden = def === '';
      ship.classList.remove('obp-offers__ship--home');
    }
  }

  /**
   * Carte « ensemble » — pastille de la variante de la PAGE : coche la carte,
   * proxe la variante (comme le constructeur), met à jour la ligne principale
   * du déroulé et recalcule le prix total.
   */
  #activateMainPick(pill) {
    const offer = pill.closest('.obp-offers__offer');
    const input = offer?.querySelector('input[type="radio"]');
    if (!offer || !input) return;
    pill.parentElement.querySelectorAll('[data-obp-main-pick]').forEach((p) => {
      p.classList.toggle('is-active', p === pill);
    });
    input.dataset.setValue = pill.dataset.setValue || '';
    if (!input.checked) input.checked = true;
    const cap = offer.querySelector('[data-obp-main-caption]');
    const mainPriceEl = offer.querySelector('[data-obp-main-price]');
    if (pill.dataset.obpMainPick === 'both') {
      // « Les 2 coffrets » : arme le 2e coffret (l'autre variante de la page) en extra2,
      // et remplace la contribution du produit de la page au total (ex. 2 coffrets = 49 €).
      input.dataset.curMain = 'both';
      input.dataset.extra2Variant = pill.dataset.main2 || '';
      input.dataset.extra2Qty = '1';
      input.dataset.mainActual = pill.dataset.bothActual || '';
      input.dataset.mainCompare = pill.dataset.bothCompare || '';
      if (mainPriceEl && pill.dataset.bothActual) mainPriceEl.textContent = pill.dataset.bothActual;
      if (cap && this.#variantInfo) {
        const names = Object.values(this.#variantInfo).flatMap((v) => (v.title.replace(/ — /g, ' - ').split(' - ')[1] || v.title).split(', '));
        this.#renderCastles(cap, names);
      }
    } else {
      input.dataset.curMain = pill.dataset.obpMainPick || '';
      // si la carte a une pastille « les 2 coffrets », retirer le 2e coffret + restaurer la base
      if (offer.querySelector('[data-obp-main-pick="both"]')) {
        delete input.dataset.extra2Variant;
        delete input.dataset.extra2Qty;
        input.dataset.mainActual = input.dataset.mainActualBase || '';
        input.dataset.mainCompare = input.dataset.mainCompareBase || '';
      }
      if (mainPriceEl && pill.dataset.price) mainPriceEl.textContent = pill.dataset.price;
      const info = this.#variantInfo?.[String(pill.dataset.obpMainPick)];
      if (cap && info) {
        const parts = info.title.replace(/ — /g, ' - ').split(' - ');
        this.#renderCastles(cap, (parts[1] || info.title).split(', '));
      }
    }
    this.#recomputeTotal(input);
    if (input.dataset.setValue) this.#proxyVariant(input.dataset.setValue);
    this.#syncGallery(input);
    this.#updateShip(offer);
  }

  /**
   * Carte « ensemble » — pastille de la variante du COMPAGNON : coche la carte,
   * arme la bonne variante compagnon (data-extra-variant), met à jour la ligne
   * compagnon du déroulé et recalcule le prix total. Ne proxe PAS (le compagnon
   * n'est pas le produit de la page) mais réaligne la variante de la page sur la
   * pastille principale active de la carte.
   */
  #activateExtraPick(pill) {
    const offer = pill.closest('.obp-offers__offer');
    const input = offer?.querySelector('input[type="radio"]');
    if (!offer || !input) return;
    pill.parentElement.querySelectorAll('[data-obp-extra-pick]').forEach((p) => {
      p.classList.toggle('is-active', p === pill);
    });
    if (!input.checked) input.checked = true;
    let extraInfo = null;
    try {
      extraInfo = input.dataset.extraInfo ? JSON.parse(input.dataset.extraInfo) : null;
    } catch (error) {
      console.error('[OBP offres] data-extra-info illisible', error);
    }
    const cap = offer.querySelector('[data-obp-extra-caption]');
    const xp = offer.querySelector('[data-obp-extra-price]');
    const xs = offer.querySelector('[data-obp-extra-compare]');
    const setLine = (title, price, compare) => {
      offer.querySelectorAll('[data-obp-extra-title]').forEach((el) => { el.textContent = (title || '').replace(/ — /g, ' - '); });
      if (xp && price) xp.textContent = price;
      if (xs) {
        if (compare) { xs.textContent = compare; xs.hidden = false; }
        else { xs.hidden = true; }
      }
    };
    if (pill.dataset.obpExtraPick === 'both') {
      // « Les 2 volumes » : arme les DEUX variantes du compagnon (ex. les 8 dessous de verre).
      input.dataset.extraVariant = pill.dataset.extra || '';
      input.dataset.extraQty = '1';
      input.dataset.extra2Variant = pill.dataset.extra2 || '';
      input.dataset.extra2Qty = '1';
      input.dataset.curExtra = 'both';
      input.dataset.extraActual = pill.dataset.bothActual || '';
      input.dataset.extraCompare = pill.dataset.bothCompare || '';
      setLine(pill.textContent.trim(), pill.dataset.bothActual, pill.dataset.bothCompare);
      offer.querySelectorAll('img[data-obp-extra-img]').forEach((el) => { el.style.visibility = 'hidden'; });
      if (cap && extraInfo) {
        const names = Object.values(extraInfo).flatMap((v) => (v.title.replace(/ — /g, ' - ').split(' - ')[1] || v.title).split(', '));
        this.#renderCastles(cap, names);
      }
    } else {
      const vid = pill.dataset.obpExtraPick || '';
      input.dataset.extraVariant = vid;
      input.dataset.extraQty = '1';
      delete input.dataset.extra2Variant;
      delete input.dataset.extra2Qty;
      input.dataset.curExtra = vid;
      // restaure la contribution compagnon de base (ex. DDV remisé à 20 € / barré 29 €)
      input.dataset.extraActual = input.dataset.extraActualBase || '';
      input.dataset.extraCompare = input.dataset.extraCompareBase || '';
      const info = extraInfo?.[vid];
      if (info) {
        // prix ligne : base fixe si définie (ex. 20 €), sinon prix réel de la variante (ex. plateau 49/65)
        const linePrice = input.dataset.extraActualBase ? input.dataset.extraActualBase : info.price;
        const lineCompare = input.dataset.extraCompareBase || (input.dataset.extraActualBase ? '' : info.compare || '');
        setLine(info.title, linePrice, lineCompare);
        offer.querySelectorAll('img[data-obp-extra-img]').forEach((el) => { el.style.visibility = ''; if (info.image) el.src = info.image; });
        if (cap) {
          const parts = info.title.replace(/ — /g, ' - ').split(' - ');
          this.#renderCastles(cap, (parts[1] || info.title).split(', '));
        }
      }
    }
    this.#recomputeTotal(input);
    // la variante de la page suit la pastille principale active de cette carte
    if (input.dataset.setValue) this.#proxyVariant(input.dataset.setValue);
    this.#updateShip(offer);
  }

  /**
   * Carte « N lots » à répartir : +/- ajuste la ligne, l'autre variante complète
   * à la somme (2 variantes complémentaires). Compose le panier via data-offer-qty
   * (1re variante) + compagnon (2e variante).
   */
  #adjustSplit(btn, delta) {
    const container = btn.closest('[data-obp-split]');
    const offer = btn.closest('.obp-offers__offer');
    const input = offer?.querySelector('input[type="radio"]');
    if (!container || !input) return;
    const total = Number(container.dataset.splitTotal || 0);
    const rows = Array.from(container.querySelectorAll('[data-obp-split-row]'));
    const row = btn.closest('[data-obp-split-row]');
    const idx = rows.indexOf(row);
    const counts = rows.map((r) => Number(r.querySelector('[data-obp-split-count]').textContent) || 0);
    let next = counts[idx] + delta;
    if (next < 0) next = 0;
    if (next > total) next = total;
    counts[idx] = next;
    if (rows.length === 2) counts[1 - idx] = total - next;
    rows.forEach((r, k) => {
      r.querySelector('[data-obp-split-count]').textContent = String(counts[k]);
      r.querySelector('[data-obp-split-dec]').disabled = counts[k] <= 0;
      r.querySelector('[data-obp-split-inc]').disabled = counts[k] >= total;
    });
    // Pas de proxy ici : la composition exacte du panier se fait dans #composeSplit
    // au moment de l'ajout (lecture des compteurs), sans re-render de variante.
    if (!input.checked) input.checked = true;
  }

  /**
   * Prix total d'une carte « ensemble » = contribution principale + compagnon.
   * Un prix « détail » fixe (ex. DDV remisé à 20 €) prime ; sinon on prend le
   * prix réel de la variante sélectionnée (ex. plateau 49/65). Idem pour le prix
   * barré (compare). Ex. plateau 49/65 + DDV 20 = 69/85 (barré 78/94).
   */
  #recomputeTotal(input) {
    if (!input.hasAttribute('data-total-mode')) return;
    const offer = input.closest('.obp-offers__offer');
    const priceEl = offer?.querySelector('.obp-offers__price');
    if (!priceEl) return;
    const parseM = (v) => {
      const t = String(v == null ? '' : v);
      const m = t.match(/(\d+)[.,](\d{2})(?!\d)/);
      if (m) return parseFloat(m[1] + '.' + m[2]);
      const m2 = t.match(/\d+/);
      return m2 ? parseFloat(m2[0]) : NaN;
    };
    let extraInfo = null;
    try {
      extraInfo = input.dataset.extraInfo ? JSON.parse(input.dataset.extraInfo) : null;
    } catch (error) { extraInfo = null; }
    const mainQty = Number(input.dataset.mainQty || 1);
    const extraQty = Number(input.dataset.extraQty || 1);
    // contribution du produit de la page
    // OBP 08/10/2026 : quand le prix vient de la variante, son prix barré
    // (compare_at_price) entre dans le barré du total. Ex. plateau Loire 49 €
    // (barré 59) + DDV 20 € (barré 29) = 69 € au lieu de 88 €, et non de 78 €.
    let mainActual = parseM(input.dataset.mainActual);
    let mainCompare = parseM(input.dataset.mainCompare);
    if (isNaN(mainActual)) {
      const info = this.#variantInfo?.[String(input.dataset.curMain)];
      mainActual = parseM(info?.price) * mainQty;
      if (isNaN(mainCompare)) {
        const c = parseM(info?.compare);
        if (!isNaN(c)) mainCompare = c * mainQty;
      }
    }
    if (isNaN(mainCompare)) mainCompare = mainActual;
    // contribution du compagnon
    let extraActual = parseM(input.dataset.extraActual);
    let extraCompare = parseM(input.dataset.extraCompare);
    if (isNaN(extraActual)) {
      const xinfo = extraInfo?.[String(input.dataset.curExtra)];
      extraActual = parseM(xinfo?.price) * extraQty;
      if (isNaN(extraCompare)) {
        const c = parseM(xinfo?.compare);
        if (!isNaN(c)) extraCompare = c * extraQty;
      }
    }
    if (isNaN(extraCompare)) extraCompare = extraActual;
    const total = mainActual + extraActual;
    const compare = mainCompare + extraCompare;
    if (isNaN(total)) return;
    const fmt = (n) => n.toFixed(2).replace('.', ',') + ' €';
    const parts = [document.createTextNode(fmt(total))];
    if (compare > total + 0.001) {
      const s = document.createElement('s');
      s.textContent = fmt(compare);
      parts.push(s);
    }
    priceEl.replaceChildren(...parts);
    // OBP 08/10/2026 : un badge en montant (« −9 € ») suit l'économie réelle
    // (barré − prix). Les badges en texte (« Le plus offert ») ne bougent pas.
    const badge = offer.querySelector('.obp-offers__badge');
    if (badge) {
      if (badge.dataset.obpBadgeAmount === undefined) {
        badge.dataset.obpBadgeAmount = /^[−-]\s*\d+(?:[.,]\d+)?\s*€$/.test(badge.textContent.trim()) ? '1' : '0';
      }
      if (badge.dataset.obpBadgeAmount === '1' && compare > total + 0.001) {
        const diff = compare - total;
        const amount = Math.abs(diff - Math.round(diff)) < 0.005 ? String(Math.round(diff)) : diff.toFixed(2).replace('.', ',');
        badge.textContent = '−' + amount + ' €';
      }
    }
  }

  /** Lit un montant affiché (« 49,00 € », « 1 049,00 € ») ; NaN si illisible. */
  #parseMoney(v) {
    const t = String(v == null ? '' : v).replace(/[\s\u00a0\u202f]/g, '');
    const m = t.match(/(\d+)[.,](\d{2})(?!\d)/);
    if (m) return parseFloat(m[1] + '.' + m[2]);
    const m2 = t.match(/\d+/);
    return m2 ? parseFloat(m2[0]) : NaN;
  }

  #formatMoney(n) {
    return n.toFixed(2).replace('.', ',') + ' €';
  }

  /** Prix d'une carte, avec le barré à côté s'il est plus élevé. */
  #setPrice(el, price, compare) {
    const p = this.#parseMoney(price);
    const c = this.#parseMoney(compare);
    if (compare && !isNaN(p) && !isNaN(c) && c > p + 0.001) {
      const s = document.createElement('s');
      s.textContent = compare;
      el.replaceChildren(document.createTextNode(price), s);
    } else {
      el.textContent = price;
    }
  }

  /**
   * Au chargement, coche la carte « simple » (qty 1, sans compagnon) qui
   * correspond à la variante déjà sélectionnée dans le formulaire — pour que
   * le constructeur reflète l'état de la page (ex. arrivée avec ?variant=).
   */
  #preselectFromVariant() {
    if (this.querySelector('input[type="radio"]:checked')) return;
    const native = this.#scope.querySelector('variant-picker input[type="radio"]:checked');
    if (!native) return;
    const match = Array.from(this.querySelectorAll('input[type="radio"][data-set-value]')).find(
      (r) =>
        r.dataset.setValue === native.value &&
        !r.dataset.extraVariant &&
        Number(r.dataset.offerQty || 1) === 1
    );
    if (match) {
      match.checked = true;
      // aligne la pill de volume de la carte, si elle en a
      const pill = Array.from(match.closest('.obp-offers__offer')?.querySelectorAll('[data-obp-volume]') || [])
        .find((p) => p.dataset.setValue === native.value);
      if (pill) this.#activatePill(pill, false);
      return;
    }
    // arrivée sur une variante non-défaut (ex. ?variant=Vol.2) : activer la pill
    // correspondante de la carte « simple » (qty 1, sans compagnon)
    const pill = Array.from(this.querySelectorAll('[data-obp-volume]')).find((p) => {
      if (p.dataset.setValue !== native.value) return false;
      const input = p.closest('.obp-offers__offer')?.querySelector('input[type="radio"]');
      return input && !input.dataset.extraVariant && Number(input.dataset.offerQty || 1) === 1;
    });
    if (pill) this.#activatePill(pill, false);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #scope() {
    return this.closest('.shopify-section') || document;
  }

  /** L'ID de variante du produit principal, lu dans le formulaire natif. */
  get #mainVariantId() {
    const form = this.#scope.querySelector('product-form-component form');
    return Number(form?.querySelector('input[name="id"]')?.value || 0);
  }

  async #addToCart(variantId, quantity) {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    const added = await response.json();
    if (added.status) {
      console.error('[OBP offres] échec de l\'ajout', variantId, added);
      return false;
    }
    console.info('[OBP offres] ajouté :', variantId, '×', quantity);
    return true;
  }

  /**
   * Compose le panier d'une carte « N lots » : chaque variante = son compteur
   * (additif au panier existant). Lit la quantité réellement ajoutée par le
   * formulaire (champ quantité) pour ne pas double-compter la variante du form.
   * Aucun proxy → aucun re-render Horizon qui réinitialiserait les compteurs.
   */
  async #composeSplit(radio) {
    const offer = radio.closest('.obp-offers__offer');
    if (!offer) return;
    const rows = Array.from(offer.querySelectorAll('[data-obp-split-row]')).map((r) => ({
      variant: Number(r.dataset.variant),
      count: Number(r.querySelector('[data-obp-split-count]').textContent) || 0,
    }));
    if (!rows.length) return;
    const formVar = this.#mainVariantId;
    const qEl = this.#scope.querySelector('product-form-component [name="quantity"]');
    const formAdded = Number(qEl?.value || 1);
    this.#busy = true;
    try {
      // Snapshot APRÈS l'ajout du formulaire (inclut formAdded sur la variante du form).
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      for (const { variant, count } of rows) {
        const line = cart.items.find((i) => Number(i.id) === variant);
        const existing = line ? line.quantity : 0;
        // delta que CETTE formule doit apporter à la variante (le form a déjà mis formAdded).
        const delta = variant === formVar ? count - formAdded : count;
        // ⚠ change.js REFUSE d'augmenter (422 « disponibilité ») sur ces variantes,
        // alors qu'add.js incrémente sans souci → augmenter via add.js, réduire via change.js.
        if (delta > 0) {
          await this.#addToCart(variant, delta);
        } else if (delta < 0 && line) {
          await fetch(Theme.routes.cart_change_url || '/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: Math.max(0, existing + delta) }),
          });
        }
      }
      const c2 = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      document.dispatchEvent(
        new CartUpdateEvent(c2, 'obp-offers-component', { source: 'obp-offers-component' })
      );
      console.info('[OBP offres] lots composés :', rows.map((r) => r.count).join('/'));
    } catch (error) {
      console.error('[OBP offres] erreur composition « lots »', error);
    } finally {
      this.#busy = false;
    }
  }

  #onCartUpdate = async (event) => {
    const data = /** @type {any} */ (event).detail?.data;
    if (!data || data.source !== 'product-form-component' || data.didError) return;
    const expectedId = this.#scope.querySelector('product-form-component')?.dataset.productId || this.dataset.productId;
    if (String(data.productId) !== String(expectedId)) return;
    if (this.#busy) return;

    const radio = this.querySelector('input[type="radio"]:checked');
    if (!radio) return;
    // Carte « N lots » à répartir : compose exactement n Vol.1 + m Vol.2 depuis les compteurs.
    if (radio.hasAttribute('data-split-mode')) {
      await this.#composeSplit(radio);
      return;
    }
    const offerQty = Number(radio.dataset.offerQty || 1);
    let extraVariant = Number(radio.dataset.extraVariant || 0);
    const extraQty = Number(radio.dataset.extraQty ?? 1);
    const extra2Variant = Number(radio.dataset.extra2Variant || 0);
    const extra2Qty = Number(radio.dataset.extra2Qty || 1);

    // Mode « swap » (page coffret Vol.1/Vol.2) : le compagnon est l'AUTRE
    // variante que celle sélectionnée dans le formulaire, résolue maintenant.
    if (radio.dataset.extraSwap) {
      try {
        const swapMap = JSON.parse(radio.dataset.extraSwap);
        const resolved = swapMap[String(this.#mainVariantId)];
        if (resolved) extraVariant = Number(resolved);
        console.info('[OBP offres] swap : compagnon résolu →', extraVariant);
      } catch (error) {
        console.error('[OBP offres] data-extra-swap illisible', error);
      }
    }

    // Quantité déjà ajoutée par le formulaire natif (en général 1).
    const alreadyAdded = Number(data.itemCount || 1);
    if (offerQty <= alreadyAdded && !(extraVariant && extraQty > 0) && !extra2Variant) return;

    this.#busy = true;
    try {
      // Une seule ligne au panier : on AJUSTE la quantité de la ligne existante
      // via cart/change.js (un simple add créerait une 2e ligne du même produit).
      // offerQty 0 = offre « coffret » : le clic ne doit PAS ajouter le produit
      // de la page (on retire ce que le formulaire vient d'ajouter), seul le
      // compagnon part au panier. Les unités présentes AVANT le clic restent.
      if (offerQty !== alreadyAdded) {
        const mainVariant = this.#mainVariantId;
        const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
        const line = cart.items.find((item) => Number(item.id) === mainVariant);
        if (line) {
          // Ce clic devait produire offerQty : on complète par rapport à ce qui
          // existait AVANT le clic (line.quantity - alreadyAdded).
          const target = line.quantity - alreadyAdded + offerQty;
          if (target > line.quantity) {
            // ⚠ change.js peut refuser une AUGMENTATION (422 « disponibilité ») sur
            // certaines variantes ; add.js incrémente sans souci.
            await this.#addToCart(mainVariant, target - line.quantity);
          } else if (target !== line.quantity) {
            const change = await fetch(Theme.routes.cart_change_url || '/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: Math.max(0, target) }),
            });
            const changed = await change.json();
            if (changed.status) console.error('[OBP offres] échec ajustement quantité', changed);
            else console.info('[OBP offres] quantité ajustée à', target);
          }
        } else {
          console.error('[OBP offres] ligne du produit principal introuvable au panier');
        }
      }
      if (extraVariant && extraQty > 0) {
        await this.#addToCart(extraVariant, extraQty);
      }
      if (extra2Variant) {
        await this.#addToCart(extra2Variant, extra2Qty);
      }
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'obp-offers-component', { source: 'obp-offers-component' })
      );
    } catch (error) {
      console.error('[OBP offres] erreur lors du complément d\'offre', error);
    } finally {
      this.#busy = false;
    }
  };
}

if (!customElements.get('obp-offers-component')) {
  customElements.define('obp-offers-component', OBPOffersComponent);
}
